/**
 * ADR-0016 / ADR-0012 — Run inventory ledger deviation scan (Safe Mode input).
 */

import { detectInventoryLedgerDeviations } from './ledgerProjectionDeviation';
import {
  enableStorageSafeMode,
  disableStorageSafeMode,
  isStorageSafeMode,
  getStorageSafeModeReason
} from '../../../infrastructure/storage/storageSafeMode';

export const INVENTORY_LEDGER_SAFE_MODE_REASON = 'inventory_ledger_deviation';

/**
 * Load live business payload for health scan.
 * Prefer async entity/blob authority over warm/sticky sync cache — the latter can
 * keep residual remainingQuantity after reproject and false-trip Safe Mode.
 * @param {string} businessId
 */
async function loadPayloadForScan(businessId) {
  let body = {};

  // Full assemble when entity authority is on (includes thin-shell snapshots)
  try {
    const { isEntityStoreAuthorityEnabled } = await import(
      '../../../infrastructure/storage/entityAggregateStore'
    );
    if (isEntityStoreAuthorityEnabled()) {
      const { readBusinessPayloadWithEntityAuthority } = await import(
        '../../../infrastructure/storage/businessPayloadAssembly'
      );
      const assembled = await readBusinessPayloadWithEntityAuthority(businessId);
      if (assembled && typeof assembled === 'object') {
        body = { ...assembled };
      }
    }
  } catch {
    /* fall through */
  }

  if (!body || Object.keys(body).length === 0) {
    const { readBestBusinessMergedPayload } = await import('../../../utils/sharedCore');
    const { parsed } = readBestBusinessMergedPayload(businessId) || {};
    body = parsed && typeof parsed === 'object' ? { ...parsed } : {};
  }

  try {
    const { readLotsFromBestSource, seedLotsReadCache } = await import(
      '../../../utils/lotsReadSource'
    );
    const fromBest = await readLotsFromBestSource(businessId);
    if (Array.isArray(fromBest) && fromBest.length) {
      body = { ...body, lots: fromBest };
      seedLotsReadCache(businessId, fromBest);
    }
  } catch {
    /* optional */
  }
  return body;
}

/**
 * @param {{ businessId: string, enableSafeMode?: boolean, clearSafeModeWhenOk?: boolean, epsilon?: number }} options
 */
export async function runInventoryLedgerHealthScan(options = {}) {
  const businessId = options.businessId;
  if (!businessId) {
    return {
      ok: false,
      error: 'business_id_missing',
      deviationCount: 0,
      report: null
    };
  }

  try {
    const parsed = await loadPayloadForScan(businessId);
    const report = detectInventoryLedgerDeviations(parsed, {
      epsilon: options.epsilon
    });

    const enableSafeMode = options.enableSafeMode !== false;
    if (!report.ok && enableSafeMode) {
      enableStorageSafeMode(
        `${INVENTORY_LEDGER_SAFE_MODE_REASON}:${report.deviationCount}`
      );
    } else if (
      report.ok &&
      options.clearSafeModeWhenOk === true &&
      isStorageSafeMode() &&
      String(getStorageSafeModeReason() || '').startsWith(INVENTORY_LEDGER_SAFE_MODE_REASON)
    ) {
      disableStorageSafeMode();
    }

    return {
      ok: report.ok,
      businessId,
      deviationCount: report.deviationCount,
      report,
      safeMode: isStorageSafeMode(),
      safeModeReason: getStorageSafeModeReason()
    };
  } catch (err) {
    return {
      ok: false,
      error: err?.message || String(err),
      deviationCount: 0,
      report: null
    };
  }
}
